import type {
  Agent,
  CalendarMeeting,
  Conversation,
  FileAttachment,
  MeetingContext,
  MeetingRange,
  SearchScope,
  ModelCatalog,
  Project,
  ProjectDetail,
  RetrievalEngineCatalog,
  StoredMessage,
} from "@/types/api"

import { getAuthHeaders, getWorkIqToken } from "./identity"

export class ApiError extends Error {
  status: number

  constructor(status: number, detail: string) {
    super(detail)
    this.name = "ApiError"
    this.status = status
  }
}

async function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  // MSAL mode: Bearer Graph token. Platform mode: nothing — the session cookie authenticates.
  for (const [key, value] of Object.entries(await getAuthHeaders())) headers.set(key, value)
  const response = await fetch(input, { ...init, headers })
  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const body = await response.json()
      if (body?.detail) detail = String(body.detail)
    } catch {
      // non-JSON error body
    }
    throw new ApiError(response.status, detail)
  }
  return response
}

/** Agent id (agents.yaml) whose requests carry the Work IQ token. */
export const WORKIQ_AGENT_ID = "work-iq"
export const MEETING_AGENT_ID = "meeting"

export interface ChatStreamRequest {
  message: string
  conversation_id: string | null
  file_attachment: FileAttachment | null
  agent_id: string | null
  model_id: string | null
  effort: string | null
  retrieval_engine: string | null
  scopes: SearchScope[]
  project_id: string | null
  meeting_range?: MeetingRange | null
  meeting?: MeetingContext | null
}

/** Open the chat SSE stream — feed the Response to parseSSEStream<ChatStreamEvent>. */
export async function streamChat(body: ChatStreamRequest, signal: AbortSignal): Promise<Response> {
  const headers: Record<string, string> = { "Content-Type": "application/json" }
  // The /work-iq and /meeting agents need a second delegated token (a different resource than Graph).
  // Only fetched for those agents; without it the backend reports Work IQ as unavailable.
  if (body.agent_id === WORKIQ_AGENT_ID || body.agent_id === MEETING_AGENT_ID) {
    const workIqToken = await getWorkIqToken()
    if (workIqToken) headers["X-WorkIQ-Token"] = workIqToken
  }
  return authFetch("/api/chat/stream", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal,
  })
}

/** The signed-in user's own meetings on one local day (YYYY-MM-DD), read from Graph. */
export async function getMeetings(date: string, timeZone: string): Promise<CalendarMeeting[]> {
  const query = new URLSearchParams({ date, tz: timeZone })
  const response = await authFetch(`/api/meetings?${query}`)
  const data = await response.json()
  return data.meetings ?? []
}

export async function getAgents(): Promise<Agent[]> {
  const response = await authFetch("/api/agents")
  return response.json()
}

/** Chat models from the LiteLLM gateway, plus the server-side default. */
export async function getModels(): Promise<ModelCatalog> {
  const response = await authFetch("/api/models")
  return response.json()
}

/** SharePoint retrieval engines this deployment can run, plus its default. */
export async function getRetrievalEngines(): Promise<RetrievalEngineCatalog> {
  const response = await authFetch("/api/retrieval-engines")
  return response.json()
}

export async function getConversations(): Promise<Conversation[]> {
  const response = await authFetch("/api/conversations")
  return response.json()
}

export async function getConversationMessages(id: string): Promise<StoredMessage[]> {
  const response = await authFetch(`/api/conversations/${id}/messages`)
  return response.json()
}

export async function deleteConversation(id: string): Promise<void> {
  await authFetch(`/api/conversations/${id}`, { method: "DELETE" })
}

export async function getProjects(): Promise<Project[]> {
  const response = await authFetch("/api/projects")
  return response.json()
}

export async function getProject(id: string): Promise<ProjectDetail> {
  const response = await authFetch(`/api/projects/${encodeURIComponent(id)}`)
  return response.json()
}

export async function createDummyChats(count: number): Promise<void> {
  await authFetch(`/api/dev/dummy-chats?count=${count}`, { method: "POST" })
}

export async function createProject(body: { name: string; description?: string }): Promise<Project> {
  const response = await authFetch("/api/projects", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  return response.json()
}

export async function updateProject(
  id: string,
  body: { name?: string; description?: string; instructions?: string },
): Promise<Project> {
  const response = await authFetch(`/api/projects/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  return response.json()
}

export async function deleteProject(
  id: string,
  body: { chat_disposition: "move_to_regular" | "delete_chats"; confirm_delete_chats?: boolean },
): Promise<void> {
  await authFetch(`/api/projects/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
}

export async function assignConversationToProject(id: string, projectId: string | null): Promise<void> {
  await authFetch(`/api/conversations/${encodeURIComponent(id)}/project`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ project_id: projectId }),
  })
}

export async function removeProjectReference(projectId: string, referenceId: string): Promise<void> {
  await authFetch(`/api/projects/${encodeURIComponent(projectId)}/references/${encodeURIComponent(referenceId)}`, {
    method: "DELETE",
  })
}

export async function setProjectReferenceHidden(projectId: string, referenceId: string, hidden: boolean): Promise<void> {
  await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/references/${encodeURIComponent(referenceId)}/hidden`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hidden }),
    },
  )
}

export async function recordProjectReferenceUse(projectId: string, referenceId: string): Promise<void> {
  await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/references/${encodeURIComponent(referenceId)}/use`,
    { method: "POST" },
  )
}

export async function uploadFile(file: File): Promise<FileAttachment> {
  const form = new FormData()
  form.append("file", file)
  // No Content-Type header — the browser sets the multipart boundary
  const response = await authFetch("/api/upload", { method: "POST", body: form })
  return response.json()
}

export async function getSuggestions(req: {
  question: string
  response: string
  source_titles: string[]
  model_id: string | null
}): Promise<string[]> {
  const response = await authFetch("/api/chat/suggestions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  })
  const data = await response.json()
  return data.suggestions ?? []
}

/** Starter questions for an empty chat in a project, based on its remembered documents. */
export async function getProjectSuggestions(projectId: string, modelId: string | null): Promise<string[]> {
  const query = modelId ? `?${new URLSearchParams({ model_id: modelId })}` : ""
  const response = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/suggestions${query}`)
  const data = await response.json()
  return data.suggestions ?? []
}

export async function getPreviewUrl(params: {
  sourceUrl: string
  driveId?: string | null
  itemId?: string | null
}): Promise<string> {
  const query = new URLSearchParams({ source_url: params.sourceUrl })
  if (params.driveId) query.set("drive_id", params.driveId)
  if (params.itemId) query.set("item_id", params.itemId)
  const response = await authFetch(`/api/preview-url?${query}`)
  const data = await response.json()
  return data.url
}

/** Fetch the document rendered as PDF bytes for the native in-app viewer.
 *  Throws (via authFetch) on any non-2xx — callers use that as the signal to
 *  fall back to the Office-Online iframe preview. */
export async function getPreviewPdfBlob(params: {
  sourceUrl: string
  driveId?: string | null
  itemId?: string | null
}): Promise<Blob> {
  const query = new URLSearchParams({ source_url: params.sourceUrl })
  if (params.driveId) query.set("drive_id", params.driveId)
  if (params.itemId) query.set("item_id", params.itemId)
  const response = await authFetch(`/api/preview-pdf?${query}`)
  return response.blob()
}

/** Profile photo of the signed-in user, or null if they have none (or it can't be fetched). */
export async function getMyPhotoBlob(): Promise<Blob | null> {
  try {
    const response = await authFetch("/api/me/photo")
    return await response.blob()
  } catch {
    return null
  }
}

/** Open the summary SSE stream — feed the Response to parseSSEStream<SummaryStreamEvent>. */
export function summarizeSource(
  params: { sourceUrl?: string | null; driveId?: string | null; itemId?: string | null },
  signal?: AbortSignal,
): Promise<Response> {
  const query = new URLSearchParams()
  if (params.driveId) query.set("drive_id", params.driveId)
  if (params.itemId) query.set("item_id", params.itemId)
  if (params.sourceUrl) query.set("source_url", params.sourceUrl)
  return authFetch(`/api/summarize-source?${query}`, { method: "POST", signal })
}
